import React, { useEffect, useMemo, useState } from 'react';

import { CookieNames } from 'common/cookies/cookies';
import connect from 'common/core/connect';
import setCookie from 'common/setCookie';
import Themes, { type Theme, calculateTheme, getThemeFromBrowserSettings } from 'common/util/theme';

export const ThemeContext = React.createContext<{
  theme: Theme;
  setTheme: React.Dispatch<React.SetStateAction<Theme>>;
} | null>(null);

type OwnProps = {
  children: React.ReactNode;
  configTheme?: Theme;
  isUserTheme?: boolean;
};

type ConnectProps = {
  cookies?: Record<string, string>;
  viewer?: { theme?: Theme };
};

type Props = OwnProps & ConnectProps;

// Extract media query logic to a custom hook
const useBrowserTheme = (initialTheme: Theme) => {
  const [browserTheme, setBrowserTheme] = useState(initialTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      return;
    }

    const handleChange = (event: MediaQueryListEvent | MediaQueryList) =>
      setBrowserTheme(event.matches ? Themes.dark : Themes.light);

    // Feature detection: use addEventListener if available (modern browsers),
    // otherwise fall back to addListener (Safari ≤13, older Chromium)
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }

    return;
  }, []);

  return browserTheme;
};

const ThemeContainer = ({ children, configTheme, cookies, isUserTheme = false, viewer }: Props) => {
  const initialBrowserTheme = useMemo(() => getThemeFromBrowserSettings(cookies), [cookies]);
  const browserTheme = useBrowserTheme(initialBrowserTheme);

  const effectiveConfigTheme = useMemo(
    () => (isUserTheme ? viewer?.theme || Themes.auto : configTheme),
    [isUserTheme, viewer, configTheme],
  );

  const [theme, setTheme] = useState(() =>
    calculateTheme({ configTheme: effectiveConfigTheme, browserTheme: initialBrowserTheme }),
  );

  // Update theme when dependencies change
  useEffect(() => {
    setTheme(calculateTheme({ configTheme: effectiveConfigTheme, browserTheme }));
  }, [browserTheme, effectiveConfigTheme]);

  // Persist browser theme to cookie
  useEffect(() => {
    setCookie(CookieNames.browserTheme, browserTheme);
  }, [browserTheme]);

  // Apply theme class to HTML element
  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove('darkTheme', 'lightTheme');
    html.classList.add(theme === 'dark' ? 'darkTheme' : 'lightTheme');
  }, [theme]);

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
};

export default connect((state: any) => ({
  cookies: state.cookies,
  viewer: state.viewer,
}))(ThemeContainer) as unknown as React.FC<OwnProps>;
